<script>
import { mapGetters } from 'vuex';
import BackButton from '../BackButton.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Thumbnail from '../Thumbnail.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Linear from './linear/index.vue';
import wootAPI from '@/v3/api/apiClient';
import Button from 'dashboard/components-next/button/Button.vue';
import BlurDropdown from 'dashboard/components-next/dropdown/BlurDropdown.vue';
import { formatFlagDisplayText } from 'shared/helpers/flagEmoji';

export default {
  components: {
    BackButton,
    InboxName,
    MoreActions,
    Thumbnail,
    SLACardLabel,
    Linear,
    Button,
    BlurDropdown,
  },
  mixins: [inboxMixin],
  props: {
    chat: {
      type: Object,
      default: () => {},
    },
    isContactPanelOpen: {
      type: Boolean,
      default: false,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    showChatList: {
      type: Boolean,
      default: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['contactPanelToggle', 'toggleChatList', 'mobileBackToList'],
  data() {
    return {
      agentName: '',
      hasIaAgent: false,
      showActionsDropdown: false,
    };
  },
  mounted() {
    this.fetchAgentName();
  },
  watch: {
    'chat.inbox_id': function(newInboxId, oldInboxId) {
      if (newInboxId && newInboxId !== oldInboxId) {
        this.fetchAgentName();
      }
    },
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      appIntegrations: 'integrations/getAppIntegrations',
    }),
    chatMetadata() {
      return this.chat.meta;
    },
    backButtonUrl() {
      const {
        params: { accountId, inbox_id: inboxId, label, teamId },
        name,
      } = this.$route;
      return conversationListPageURL({
        accountId,
        inboxId,
        label,
        teamId,
        conversationType: name === 'conversation_mentions' ? 'mention' : '',
      });
    },
    isHMACVerified() {
      if (!this.isAWebWidgetInbox) {
        return true;
      }
      return this.chatMetadata.hmac_verified;
    },
    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.chat.meta.sender.id
      );
    },
    isSnoozed() {
      return this.currentChat.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
    snoozedDisplayText() {
      const { snoozed_until: snoozedUntil } = this.currentChat;
      if (snoozedUntil) {
        return `${this.$t(
          'CONVERSATION.HEADER.SNOOZED_UNTIL'
        )} ${snoozedReopenTime(snoozedUntil)}`;
      }
      return this.$t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
    },
    contactPanelToggleText() {
      return `${
        this.isContactPanelOpen
          ? this.$t('CONVERSATION.HEADER.CLOSE')
          : this.$t('CONVERSATION.HEADER.OPEN')
      } ${this.$t('CONVERSATION.HEADER.DETAILS')}`;
    },
    inbox() {
      const { inbox_id: inboxId } = this.chat;
      return this.$store.getters['inboxes/getInbox'](inboxId);
    },
    hasMultipleInboxes() {
      return this.$store.getters['inboxes/getInboxes'].length > 1;
    },
    hasSlaPolicyId() {
      return this.chat?.sla_policy_id;
    },
    isLinearIntegrationEnabled() {
      return this.appIntegrations.find(
        integration => integration.id === 'linear' && !!integration.hooks.length
      );
    },
    isLinearFeatureEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.LINEAR
      );
    },
    contactLabels() {
      return this.$store.getters['contactLabels/getContactLabels'](this.chat?.meta?.sender?.id) || [];
    },
    isConversationOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    isAdmin() {
      return this.$store.getters.getCurrentUserRole === 'administrator';
    },
    isConversationResolved() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.RESOLVED;
    },
    isConversationPending() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.PENDING;
    },
    isConversationSnoozed() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
    displayContactName() {
      return this.currentContact.name?.replace(/\(GROUP\)/gi, '').trim() || this.currentContact.name;
    },
    displayContactNameHtml() {
      return formatFlagDisplayText(this.displayContactName || '');
    },
    actionsDropdownItems() {
      const items = [];

      // Resolve action - show if conversation is open
      if (this.isConversationOpen) {
        items.push({
          id: 'resolve',
          title: this.$t('CONVERSATION.HEADER.RESOLVE_ACTION'),
          icon: 'check'
        });
      }

      // Reopen action - show if conversation is resolved
      if (this.isConversationResolved) {
        items.push({
          id: 'reopen',
          title: this.$t('CONVERSATION.HEADER.REOPEN_ACTION'),
          icon: 'rotate-ccw'
        });
      }

      // Open action - show if conversation is pending or snoozed
      if (this.isConversationPending || this.isConversationSnoozed) {
        items.push({
          id: 'open',
          title: this.$t('CONVERSATION.HEADER.OPEN_ACTION'),
          icon: 'folder-open'
        });
      }

      // Snooze action - show if not pending
      if (!this.isConversationPending) {
        items.push({
          id: 'snooze',
          title: this.$t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL'),
          icon: 'alarm-clock-minus'
        });
      }

      // Mark pending action - show if not pending
      if (!this.isConversationPending) {
        items.push({
          id: 'mark-pending',
          title: this.$t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING'),
          icon: 'circle-dot-dashed'
        });
      }

      return items;
    },
  },
  methods: {
    getCookie(name) {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? decodeURIComponent(match[2]) : null;
    },
    getAuthHeaders() {
      const cookie = this.getCookie('cw_d_session_info');
      if (!cookie) return {};
      try {
        const sessionInfo = JSON.parse(cookie);
        return {
          'access-token': sessionInfo['access-token'],
          'client': sessionInfo['client'],
          'uid': sessionInfo['uid']
        };
      } catch (e) {
        console.error('Error parsing cookie:', e);
        return {};
      }
    },
    async fetchAgentName() {
      const inboxId = this.chat?.inbox_id;
      if (!inboxId) return;
      
      try {
        const headers = this.getAuthHeaders();
        const response = await wootAPI.get(
          '/api/v1/inboxmeta/fetch_agentis',
          {
            params: { inbox_id: inboxId },
            headers
          }
        );
        
        const hasAgent = response.data && response.data.agent && response.data.agent.trim() !== '';
        this.hasIaAgent = hasAgent;
        
        if (hasAgent) {
          this.agentName = `${response.data.agent} (IA)`;
        } else {
          this.agentName = '';
        }
      } catch (error) {
        console.error('Error fetching agent name:', error);
        this.agentName = '';
        this.hasIaAgent = false;
      }
    },
    toggleConversationStatus(status) {
      this.$store.dispatch('toggleStatus', {
        conversationId: this.currentChat.id,
        status,
      });
    },
    toggleActionsDropdown() {
      this.showActionsDropdown = !this.showActionsDropdown;
    },
    closeActionsDropdown() {
      setTimeout(() => {
        this.showActionsDropdown = false;
      }, 50);
    },
    openSnoozeModal() {
      const ninja = document.querySelector('ninja-keys');
      ninja.open({ parent: 'snooze_conversation' });
    },
    resolveConversation() {
      this.toggleConversationStatus(wootConstants.STATUS_TYPE.RESOLVED);
    },
    reopenConversation() {
      this.toggleConversationStatus(wootConstants.STATUS_TYPE.OPEN);
    },
    markPending() {
      this.toggleConversationStatus(wootConstants.STATUS_TYPE.PENDING);
    },
    handleActionsDropdownSelect(item) {
      this.closeActionsDropdown();

      switch(item.id) {
        case 'resolve':
          this.resolveConversation();
          break;
        case 'reopen':
          this.reopenConversation();
          break;
        case 'open':
          this.reopenConversation();
          break;
        case 'snooze':
          this.openSnoozeModal();
          break;
        case 'mark-pending':
          this.markPending();
          break;
      }
    },
  },
};
</script>

<template>
  <div
    class="flex items-center justify-between p-3 bg-n-solid-1 border-b border-n-weak cursor-pointer"
    @click="$emit('contactPanelToggle')"
  >
    <div
      class="flex items-center justify-start flex-1 w-full min-w-0"
    >
      <div class="flex items-center justify-start max-w-full min-w-0 w-fit">
        <!-- Mobile back button -->
        <Button
          v-if="isMobile"
          icon="i-lucide-arrow-left"
          size="sm"
          color="slate"
          variant="ghost"
          class="md:hidden !p-0 !px-0 !py-0 !w-8 !h-8 !min-w-8 !min-h-8 !rounded-full grid place-items-center !leading-none !outline-0 border-none !text-n-slate-11 text-base ltr:mr-2 rtl:ml-2"
          @click.stop="$emit('mobileBackToList')"
        />
        <!-- Desktop: Show chat list toggle button -->
        <Button
          v-if="!showChatList && !isMobile"
          v-tooltip.bottom="'Mostrar conversas'"
          icon="i-lucide-arrow-right-to-line"
          size="sm"
          color="slate"
          variant="ghost"
          class="!p-0 !px-0 !py-0 !w-8 !h-8 !min-w-8 !min-h-8 !rounded-full grid place-items-center !leading-none !outline-0 border-none !text-n-slate-11 text-base ltr:mr-2 rtl:ml-2"
          @click.stop="$emit('toggleChatList')"
        />
        <BackButton
          v-if="showBackButton"
          :back-url="backButtonUrl"
          class="ltr:mr-2 rtl:ml-2"
          hide-text-on-mobile
          @click.stop
        />
        <Thumbnail
          :src="currentContact.thumbnail"
          :badge="inboxBadge"
          :username="currentContact.name"
          :status="currentContact.availability_status"
        />
        <div
          class="flex flex-row items-center min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2 w-fit gap-1"
        >
          <div
            class="flex flex-row items-center max-w-full gap-1 p-0 m-0 w-fit"
          >
            <span
              class="text-base font-medium leading-tight text-n-slate-12 overflow-hidden whitespace-nowrap text-ellipsis min-w-0"
              v-dompurify-html="displayContactNameHtml"
            ></span>
            <fluent-icon
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              size="14"
              class="text-n-amber-10 my-0 mx-0 min-w-[14px]"
              icon="warning"
            />
          </div>

          <div
            class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
          >
            <InboxName v-if="hasMultipleInboxes" :inbox="inbox" />
            <div
              v-if="agentName"
              class="inline-flex items-center py-1 px-2 leading-3 whitespace-nowrap bg-woot-500 text-white text-xs my-0 mr-2.5 rounded-full"
            >
              {{ agentName }}
            </div>
            <!-- <woot-button
              class="p-0"
              size="small"
              variant="link"
              @click="$emit('contactPanelToggle')"
            >
              {{ contactPanelToggleText }}
            </woot-button> -->
          </div>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
      <div
        class="flex flex-row items-center justify-end flex-grow gap-1.5 header-actions-wrap"
        :class="{ 'justify-end': isContactPanelOpen }"
      >
        <SLACardLabel v-if="hasSlaPolicyId" :chat="chat" show-extended-info />
        <Linear
          v-if="isLinearIntegrationEnabled && isLinearFeatureEnabled"
          :conversation-id="currentChat.id"
        />
        <!-- Actions Dropdown - Only show when sidebar is hidden -->
        <div v-if="!isContactPanelOpen" class="relative flex items-center" @click.stop>
          <div class="flex items-center rounded-lg button-group outline-1 outline outline-n-container">
            <Button
              label="Ações"
              size="sm"
              color="slate"
              class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
              @click="toggleActionsDropdown"
            />
            <Button
              icon="i-lucide-chevron-down"
              size="sm"
              class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
              color="slate"
              trailing-icon
              @click="toggleActionsDropdown"
            />
          </div>

          <!-- Dropdown Menu -->
          <BlurDropdown
            v-if="showActionsDropdown"
            :items="actionsDropdownItems"
            :search="false"
            :clear="false"
            @select="handleActionsDropdownSelect"
            @close="closeActionsDropdown"
          />
        </div>

        <MoreActions
          :conversation-id="currentChat.id"
          @contact-panel-toggle="$emit('contactPanelToggle')"
          @click.stop
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.conversation--header--actions {
  ::v-deep .inbox--name {
    @apply m-0;
  }
}
</style>
